import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Wand, Check, Users, MessageSquare, Mail } from 'lucide-react';
import api from '../services/api';
import eventService from '../services/eventService';
import familyMemberService, { FAMILY_MEMBER_COLORS } from '../services/familyMemberService';

const STORAGE_KEY = 'onboarding_step';
const TOTAL_STEPS = 3;

interface OnboardingFlowProps {
    onComplete: () => void;
}

// ── Step indicator ───────────────────────────────────────────────────────────

const StepDots: React.FC<{ current: number; total: number }> = ({ current, total }) => (
    <div className="flex items-center justify-center gap-2" data-testid="onboarding-step-dots">
        {Array.from({ length: total }).map((_, i) => (
            <div
                key={i}
                data-testid={`onboarding-dot-${i}`}
                data-active={i === current ? 'true' : 'false'}
                className={`rounded-full transition-all duration-200 ${
                    i === current
                        ? 'w-6 h-2'
                        : i < current
                        ? 'w-2 h-2'
                        : 'w-2 h-2 bg-warm-300'
                }`}
                style={i <= current ? { backgroundColor: 'var(--accent)' } : undefined}
            />
        ))}
    </div>
);

// ── Shared button styles ─────────────────────────────────────────────────────

const PrimaryButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    'data-testid'?: string;
    children: React.ReactNode;
}> = ({ onClick, disabled, loading, 'data-testid': testId, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled || loading}
        data-testid={testId}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-surface)' }}
    >
        {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
            children
        )}
    </button>
);

// ── Shared footer (Back / Skip / Continue) ───────────────────────────────────

const StepFooter: React.FC<{
    onBack: () => void;
    onContinue: () => void;
    onSkip: () => void;
    saving?: boolean;
    skipLabel?: string;
    isFirst?: boolean;
}> = ({ onBack, onContinue, onSkip, saving, skipLabel, isFirst }) => (
    <div className="flex items-center justify-between pt-2">
        <button
            type="button"
            onClick={onBack}
            data-testid="onboarding-back"
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            style={{ visibility: isFirst ? 'hidden' : undefined }}
        >
            Back
        </button>
        <div className="flex items-center gap-3">
            <button
                type="button"
                onClick={onSkip}
                data-testid="onboarding-skip-step"
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
                {skipLabel ?? 'Skip'}
            </button>
            <PrimaryButton onClick={onContinue} loading={saving} data-testid="onboarding-continue">
                Continue <ArrowRight className="w-4 h-4" />
            </PrimaryButton>
        </div>
    </div>
);

// ── Step 0: Family Members ───────────────────────────────────────────────────

interface AddedMember {
    id: string;
    name: string;
    color: string;
}

const FamilyMembersStep: React.FC<{
    onNext: () => void;
    onBack: () => void;
    onSkip: () => void;
    isFirst?: boolean;
}> = ({ onNext, onBack, onSkip, isFirst }) => {
    const [members, setMembers] = useState<AddedMember[]>([]);
    const [name, setName] = useState('');
    const [color, setColor] = useState<string>(FAMILY_MEMBER_COLORS[0]);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleAdd = async () => {
        if (!name.trim()) {
            setError('Please enter a name');
            return;
        }
        setAdding(true);
        setError(null);
        try {
            const member = await familyMemberService.create({ name: name.trim(), color });
            setMembers((prev) => [...prev, member]);
            setName('');
            // Auto-advance to next color for convenience
            const idx = FAMILY_MEMBER_COLORS.findIndex((c) => c === color);
            setColor(FAMILY_MEMBER_COLORS[(idx + 1) % FAMILY_MEMBER_COLORS.length]);
        } catch {
            setError('Could not add family member. Try again or skip for now.');
        } finally {
            setAdding(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <div className="flex flex-col" data-testid="onboarding-step-1">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-bg)' }}>
                    <Users className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Who's in your family?</h2>
                    <p className="text-sm text-gray-500">Add household members to tag events and filter your calendar.</p>
                </div>
            </div>

            {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="onboarding-member-error">
                    {error}
                </p>
            )}

            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                <input
                    type="text"
                    placeholder="Name (e.g. Sarah)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={adding}
                    data-testid="onboarding-member-name"
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2" style={{ borderColor: 'var(--border)', outlineColor: 'var(--accent-border)' }}
                />
                {/* Color swatches */}
                <div className="flex gap-2 flex-wrap">
                    {FAMILY_MEMBER_COLORS.map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setColor(c)}
                            aria-label={`Select color ${c}`}
                            data-testid={`onboarding-member-color-${c.replace('#', '')}`}
                            className={`w-7 h-7 rounded-full transition-all ${
                                color === c
                                    ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                                    : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={adding || !name.trim()}
                    data-testid="onboarding-member-add"
                    className="w-full px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-surface)' }}
                >
                    {adding ? 'Adding…' : '+ Add'}
                </button>
            </div>

            {members.length > 0 && (
                <div className="mb-4 space-y-2" data-testid="onboarding-members-list">
                    {members.map((m) => (
                        <div key={m.id} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                            <span
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: m.color }}
                            />
                            <p className="text-sm font-medium text-gray-800">{m.name}</p>
                        </div>
                    ))}
                </div>
            )}

            <StepFooter
                onBack={onBack}
                onContinue={onNext}
                onSkip={onSkip}
                skipLabel={members.length === 0 ? 'Skip for now' : undefined}
                isFirst={isFirst}
            />
        </div>
    );
};

// ── Step 1: Connect (email + WhatsApp) ───────────────────────────────────────

const ConnectStep: React.FC<{
    onNext: () => void;
    onBack: () => void;
    onSkip: () => void;
}> = ({ onNext, onBack, onSkip }) => {
    return (
        <div className="flex flex-col" data-testid="onboarding-step-connect">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-bg)' }}>
                    <Mail className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Add events without opening the app</h2>
                    <p className="text-sm text-gray-500">kinroo.ai works where you already communicate.</p>
                </div>
            </div>

            <div className="space-y-3 mb-6">
                {/* Email forward */}
                <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ backgroundColor: 'var(--accent-bg)', borderColor: 'var(--accent-border)' }}>
                    <Mail className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                    <div>
                        <p className="text-sm font-semibold text-gray-800">Forward any email</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Got a sports schedule or school notice? Forward it to{' '}
                            <span className="font-medium" style={{ color: 'var(--accent)' }}>add@kinroo.ai</span> and we'll add the events automatically.
                        </p>
                    </div>
                </div>

                {/* SMS */}
                <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ backgroundColor: 'var(--bg-app)', borderColor: 'var(--border)' }}>
                    <MessageSquare className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--text-muted)' }} />
                    <div>
                        <p className="text-sm font-semibold text-gray-800">Text (SMS)</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Link your phone in Settings → Connect to text events to kinroo.ai from anywhere.
                        </p>
                    </div>
                </div>
            </div>

            <StepFooter
                onBack={onBack}
                onContinue={onNext}
                onSkip={onSkip}
                skipLabel="Got it, skip"
            />
        </div>
    );
};

// ── Step 2: Try It ───────────────────────────────────────────────────────────

const TryItStep: React.FC<{
    onFinish: () => void;
    onBack: () => void;
    isFirst?: boolean;
}> = ({ onFinish, onBack, isFirst }) => {
    const [input, setInput] = useState('Add dentist appointment on Thursday at 10am');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleTry = async () => {
        if (!input.trim()) return;
        setStatus('loading');
        setMessage('');
        try {
            const result = await eventService.nlpCommand(input.trim());
            if (result.intent === 'create' || result.intent === 'update') {
                setStatus('success');
                setMessage(result.message || 'Event added to your calendar!');
            } else {
                setStatus('success');
                setMessage(result.message || 'Done!');
            }
        } catch {
            setStatus('error');
            setMessage('Something went wrong. You can try again from the calendar.');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleTry();
    };

    return (
        <div className="flex flex-col" data-testid="onboarding-step-0">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-bg)' }}>
                    <Wand className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Try it out</h2>
                    <p className="text-sm text-gray-500">kinroo.ai understands plain English. Give it a try.</p>
                </div>
            </div>

            {status === 'idle' || status === 'loading' ? (
                <div className="mb-6">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={status === 'loading'}
                            data-testid="onboarding-nlp-input"
                            className="flex-1 px-3 py-2.5 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2" style={{ borderColor: 'var(--border)' }}
                            placeholder="Add dentist appointment on Thursday at 10am"
                        />
                        <button
                            type="button"
                            onClick={handleTry}
                            disabled={status === 'loading' || !input.trim()}
                            data-testid="onboarding-nlp-send"
                            className="px-4 py-2.5 text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-surface)' }}
                        >
                            {status === 'loading' ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                'Send'
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 ml-1">
                        Edit the example or type your own.
                    </p>
                </div>
            ) : status === 'success' ? (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3" data-testid="onboarding-nlp-success">
                    <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-green-800">Event created!</p>
                        <p className="text-sm text-green-700 mt-0.5">{message}</p>
                    </div>
                </div>
            ) : (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="onboarding-nlp-error">
                    <p className="text-sm text-amber-800">{message}</p>
                </div>
            )}

            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    data-testid="onboarding-back"
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                    style={{ visibility: isFirst ? 'hidden' : undefined }}
                >
                    Back
                </button>
                <PrimaryButton onClick={onFinish} data-testid="onboarding-finish">
                    {isFirst
                        ? (status === 'success' ? 'Continue' : 'Skip for now')
                        : (status === 'success' ? 'Go to my calendar' : 'Skip, go to calendar')
                    }{' '}
                    <ArrowRight className="w-4 h-4" />
                </PrimaryButton>
            </div>
        </div>
    );
};

// ── Main orchestrator ────────────────────────────────────────────────────────

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
    const [step, setStep] = useState<number>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        const parsed = saved ? parseInt(saved, 10) : 0;
        return isNaN(parsed) || parsed < 0 || parsed >= TOTAL_STEPS ? 0 : parsed;
    });
    const [completing, setCompleting] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, String(step));
    }, [step]);

    const markComplete = useCallback(async () => {
        setCompleting(true);
        try {
            await api.post('/api/settings/complete-onboarding');
        } catch {
            // Best-effort — don't block the user
        } finally {
            localStorage.removeItem(STORAGE_KEY);
            setCompleting(false);
            onComplete();
        }
    }, [onComplete]);

    const goNext = useCallback(() => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)), []);
    const goBack = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

    const renderStep = () => {
        switch (step) {
            case 0:
                return <FamilyMembersStep onNext={goNext} onBack={goBack} onSkip={goNext} isFirst />;
            case 1:
                return <TryItStep onFinish={goNext} onBack={goBack} />;
            case 2:
                return (
                    <ConnectStep
                        onNext={markComplete}
                        onBack={goBack}
                        onSkip={markComplete}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
            data-testid="onboarding-overlay"
        >
            <div
                className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8"
                data-testid="onboarding-card"
            >
                <button
                    type="button"
                    onClick={markComplete}
                    disabled={completing}
                    aria-label="Skip setup"
                    data-testid="onboarding-close"
                    className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="mb-6">
                    <StepDots current={step} total={TOTAL_STEPS} />
                </div>

                {renderStep()}
            </div>
        </div>
    );
};

export default OnboardingFlow;
